import React, { useState, useRef } from 'react';
import { TimelineNode } from '../App';
import Icon from './Icon';
import './AddNodePanel.css';

/**
 * AddNodePanel 组件
 * 
 * 功能：
 * - 新增时间轴节点的表单面板
 * - 支持附件上传（图片、视频、音频）
 * - 使用 localStorage 持久化节点数据
 * 
 * 特性：
 * - 完整的表单验证
 * - 附件预览功能
 * - 实时保存到 localStorage
 */

interface AddNodePanelProps {
  onClose: () => void; // 关闭回调
  onSave: (node: TimelineNode) => void; // 保存回调
  editingNode?: TimelineNode | null; // 正在编辑的节点（可选）
  showToast?: (message: string, type?: 'success' | 'error' | 'warning') => void; // Toast 提示回调
}

const AddNodePanel: React.FC<AddNodePanelProps> = ({ onClose, onSave, editingNode, showToast }) => {
  // 表单状态
  const [formData, setFormData] = useState({
    date: editingNode?.date || '',
    title: editingNode?.title || '',
    description: editingNode?.description || '',
    perspective: editingNode?.perspective || 'male' as 'male' | 'female',
    stage: editingNode?.stage || '相识' as string,
    icon: editingNode?.icon || 'user' as 'user' | 'heart' | 'love' | 'star' | 'landmark' | 'calendar',
    heartRate: editingNode?.heartRate || 72
  });

  // 附件状态
  const [attachments, setAttachments] = useState<Array<{
    type: 'image' | 'video' | 'audio';
    url: string;
    caption?: string;
  }>>(editingNode?.attachments || []);

  // 文件输入引用
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 自定义心率输入框显示状态
  const [showCustomHeartRate, setShowCustomHeartRate] = useState(false);

  /**
   * 处理表单输入变化
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'heartRate' ? parseInt(value) : value
    }));
  };

  /**
   * 处理文件上传
   */
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newAttachments = Array.from(files).map(file => {
      // 判断文件类型
      let type: 'image' | 'video' | 'audio' = 'image';
      if (file.type.startsWith('video/')) {
        type = 'video';
      } else if (file.type.startsWith('audio/')) {
        type = 'audio';
      }

      // 创建文件 URL（实际项目中应该上传到服务器）
      const url = URL.createObjectURL(file);

      return {
        type,
        url,
        caption: file.name
      };
    });

    setAttachments(prev => [...prev, ...newAttachments]);
  };

  /**
   * 移除附件
   */
  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  /**
   * 保存节点
   */
  const handleSave = () => {
    // 表单验证
    if (!formData.date || !formData.title || !formData.description) {
      showToast?.('为保证记忆完整性，请把补充一下主题标题、日期和描述吧', 'warning');
      return;
    }

    // 创建新节点或更新现有节点
    const newNode: TimelineNode = {
      id: editingNode?.id || Date.now().toString(),
      date: formData.date,
      title: formData.title,
      description: formData.description,
      icon: formData.icon,
      perspective: formData.perspective,
      stage: formData.stage,
      heartRate: formData.heartRate,
      attachments: attachments.length > 0 ? attachments : undefined
    };

    // 保存到 localStorage（新增模式时）
    if (!editingNode) {
      const existingNodes = JSON.parse(localStorage.getItem('timelineNodes') || '[]');
      localStorage.setItem('timelineNodes', JSON.stringify([...existingNodes, newNode]));
    }

    // 调用保存回调
    onSave(newNode);
    onClose();
  };

  /**
   * 点击上传按钮
   */
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="add-node-overlay" onClick={onClose}>
      <div className="add-node-panel" onClick={(e) => e.stopPropagation()}>
        <div className="add-node-header">
          <h3>{editingNode ? '修饰记忆' : '记一下'}</h3>
          <button className="add-node-close" onClick={onClose}>×</button>
        </div>
        
        <div className="add-node-body">
          {/* 日期 */}
          <div className="form-group">
            <label>日期 *</label>
            <input
              type="datetime-local"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
            />
          </div>

          {/* 标题 */}
          <div className="form-group">
            <label>标题 *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="总结一下是什么事吧"
            />
          </div>

          {/* 描述 */}
          <div className="form-group">
            <label>描述 *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="留下点只有我们知道的小秘密"
              rows={4}
            />
          </div>

          {/* 视角 */}
          <div className="form-group">
            <label>视角</label>
            <div className="button-group">
              <button 
                type="button"
                className={`choice-button ${formData.perspective === 'male' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, perspective: 'male'})}
              >
                <Icon type="male" />
                男
              </button>
              <button 
                type="button"
                className={`choice-button ${formData.perspective === 'female' ? 'active perspective-female' : ''}`}
                onClick={() => setFormData({...formData, perspective: 'female'})}
              >
                <Icon type="female" />
                女
              </button>
            </div>
          </div>

          {/* 阶段 */}
          <div className="form-group">
            <label>阶段</label>
            <div className="stage-group">
              <button 
                type="button"
                className={`stage-button ${formData.stage === '相识' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, stage: '相识'})}
              >
                <Icon type="stage-met" />
                <span>相识</span>
              </button>
              <button 
                type="button"
                className={`stage-button ${formData.stage === '相知' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, stage: '相知'})}
              >
                <Icon type="stage-know" />
                <span>相知</span>
              </button>
              <button 
                type="button"
                className={`stage-button ${formData.stage === '相恋' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, stage: '相恋'})}
              >
                <Icon type="stage-love" />
                <span>相恋</span>
              </button>
              <button 
                type="button"
                className={`stage-button ${formData.stage === '相守' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, stage: '相守'})}
              >
                <Icon type="stage-together" />
                <span>相守</span>
              </button>
            </div>
          </div>

          {/* 图标 */}
          <div className="form-group">
            <label>图标</label>
            <div className="icon-group">
              <button 
                type="button"
                className={`icon-button ${formData.icon === 'user' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, icon: 'user'})}
                title="与人相关"
              >
                <Icon type="user" />
              </button>
              <button 
                type="button"
                className={`icon-button ${formData.icon === 'heart' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, icon: 'heart'})}
                title="心动时刻"
              >
                <Icon type="heart" />
              </button>
              <button 
                type="button"
                className={`icon-button ${formData.icon === 'love' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, icon: 'love'})}
                title="确定心意"
              >
                <Icon type="love" />
              </button>
              <button 
                type="button"
                className={`icon-button ${formData.icon === 'star' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, icon: 'star'})}
                title="星星星星"
              >
                <Icon type="star" />
              </button>
              <button 
                type="button"
                className={`icon-button ${formData.icon === 'landmark' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, icon: 'landmark'})}
                title="这个地方"
              >
                <Icon type="landmark" />
              </button>
              <button 
                type="button"
                className={`icon-button ${formData.icon === 'calendar' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, icon: 'calendar'})}
                title="这个时间"
              >
                <Icon type="calendar" />
              </button>
            </div>
          </div>

          {/* 心跳速率 */}
          <div className="form-group">
            <label>心跳速率</label>
            <div className="heart-rate-group">
              <button 
                type="button"
                className={`heart-rate-button ${formData.heartRate === 50 ? 'active' : ''}`}
                onClick={() => setFormData({...formData, heartRate: 50})}
              >
                <Icon type="heart-rate-sad" />
                <span>低落</span>
                <span className="heart-rate-value">50</span>
              </button>
              <button 
                type="button"
                className={`heart-rate-button ${formData.heartRate === 70 ? 'active' : ''}`}
                onClick={() => setFormData({...formData, heartRate: 70})}
              >
                <Icon type="heart-rate-calm" />
                <span>平静</span>
                <span className="heart-rate-value">70</span>
              </button>
              <button 
                type="button"
                className={`heart-rate-button ${formData.heartRate === 90 ? 'active' : ''}`}
                onClick={() => setFormData({...formData, heartRate: 90})}
              >
                <Icon type="heart-rate-happy" />
                <span>开心</span>
                <span className="heart-rate-value">90</span>
              </button>
              <button 
                type="button"
                className={`heart-rate-button ${formData.heartRate === 120 ? 'active' : ''}`}
                onClick={() => setFormData({...formData, heartRate: 120})}
              >
                <Icon type="heart-rate-excited" />
                <span>激动</span>
                <span className="heart-rate-value">120</span>
              </button>
              <button 
                type="button"
                className={`heart-rate-button ${![50, 70, 90, 120].includes(formData.heartRate) ? 'active' : ''}`}
                onClick={() => setShowCustomHeartRate(true)}
              >
                <Icon type="heart-rate-custom" />
                <span>自定义</span>
                <span className="heart-rate-value">{formData.heartRate}</span>
              </button>
            </div>
            {showCustomHeartRate && (
              <div className="custom-heart-rate">
                <input
                  type="number"
                  value={formData.heartRate}
                  onChange={(e) => setFormData({...formData, heartRate: parseInt(e.target.value) || 0})}
                  min={40}
                  max={200}
                  placeholder="40-200"
                />
                <button 
                  type="button" 
                  className="custom-button"
                  onClick={() => setShowCustomHeartRate(false)}
                >
                  确认
                </button>
                <button 
                  type="button" 
                  className="custom-button"
                  onClick={() => {
                    setShowCustomHeartRate(false);
                    setFormData({...formData, heartRate: 70});
                  }}
                >
                  取消
                </button>
              </div>
            )}
          </div>

          {/* 附件上传 */}
          <div className="form-group">
            <label>附件（可选）</label>
            <button className="upload-button" onClick={handleUploadClick}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
              </svg>
              <span>上传文件</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,audio/*"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            <div className="upload-hint">是否有其他照片、视频或声音需要一并记录呢</div>
          </div>

          {/* 附件列表 */}
          {attachments.length > 0 && (
            <div className="attachments-list">
              {attachments.map((attachment, index) => (
                <div key={index} className="attachment-item">
                  <div className="attachment-info">
                    <span className="attachment-type">{attachment.type}</span>
                    <span className="attachment-caption">{attachment.caption}</span>
                  </div>
                  <button
                    className="attachment-remove"
                    onClick={() => handleRemoveAttachment(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="add-node-footer">
          <button className="btn-cancel" onClick={onClose}>取消</button>
          <button className="btn-save" onClick={handleSave}>保存</button>
        </div>
      </div>
    </div>
  );
};

export default AddNodePanel;